import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { publicApi } from "../lib/api";
import { Rocket, ShieldCheck, CheckCircle2, XCircle, Clock, Upload } from "lucide-react";

import { _t, _locale } from "@/lib/i18n";
const STATE_LABELS = {
  BOZZA: _t("In preparazione"),
  EMESSO: _t("Emesso"),
  IN_VIAGGIO: _t("In viaggio"),
  DISPONIBILE: _t("Attivo"),
  RISCATTATO: _t("Riscattato"),
  SCADUTO: _t("Scaduto"),
  SOSPESO: _t("Sospeso"),
  ANNULLATO: _t("Annullato"),
  RIMBORSATO: _t("Rimborsato"),
};

const STATE_COLOR = {
  EMESSO: "text-yellow-400",
  IN_VIAGGIO: _t("text-[#33B2FF]"),
  DISPONIBILE: "text-green-400",
  RISCATTATO: "text-white/60",
  SCADUTO: "text-red-400",
  SOSPESO: "text-red-400",
  ANNULLATO: "text-white/50",
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(_locale(), { day: "2-digit", month: "long", year: "numeric" }); } catch { return iso; }
}

export default function VoucherVerify() {
  const { public_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await publicApi.get(`/verifica-voucher/${public_id}`);
        setData(res);
      } catch (e) {
        setData({ exists: false, reason: _t("Errore di rete") });
      } finally { setLoading(false); }
    })();
  }, [public_id]);

  const uploadPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadResult({ loading: true });
    try {
      const buf = await f.arrayBuffer();
      const { data: res } = await publicApi.post("/verifica-voucher/upload", buf, {
        headers: { "Content-Type": "application/pdf" },
      });
      setUploadResult(res);
    } catch (err) {
      setUploadResult({ match: false, reason: _t("Errore di verifica") });
    }
    e.target.value = "";
  };

  const v = data?.voucher;
  const stateLabel = v && (STATE_LABELS[v.state] || v.state);

  return (
    <div className="min-h-screen">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Rocket className="w-5 h-5 text-[#FF5722] -rotate-45" />
            <div className="font-display text-2xl text-white">{_t("Time Capsule")}</div>
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">{_t("Verifica ufficiale")}</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-3">{_t("Verifica voucher")}</div>
        <h1 className="font-display text-5xl text-white mb-8">
          {loading ? _t("Verifica in corso...") : data?.exists ? _t("Voucher registrato") : _t("Voucher non trovato")}
        </h1>

        {loading ? null : !data?.exists ? (
          <div className="tc-card p-8" data-testid="voucher-not-found">
            <XCircle className="w-10 h-10 text-red-400 mb-4" />
            <p className="text-white/70">{_t("Nessun voucher risulta registrato con l'ID")}{" "}<span className="font-mono-launch text-white">{public_id}</span>{_t(". Verifica il numero o il QR code.")}</p>
          </div>
        ) : (
          <div className="tc-card p-8 md:p-10" data-testid="voucher-details">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">{_t("Numero voucher")}</div>
                <div className="font-mono-launch text-2xl text-white">{v.public_id}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">{_t("Stato")}</div>
                <div className={`text-lg font-medium ${STATE_COLOR[v.state] || "text-white"}`}>{stateLabel}</div>
              </div>
            </div>

            {v.is_demo && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
                <strong>{_t("DEMO — Nessun valore monetario reale.")}</strong>{" "}{_t("Questo voucher è generato in ambiente di prova.")}</div>
            )}

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <Field label={_t("Valore nominale")} value={`€ ${v.face_value.toFixed(2)}`} highlight />
              <Field label={_t("Valuta")} value={v.currency.toUpperCase()} />
              <Field label={_t("Emesso il")} value={formatDate(v.issued_at)} />
              <Field label={_t("Riscattabile dal")} value={formatDate(v.available_from)} />
              <Field label={_t("Scadenza")} value={formatDate(v.expires_at)} />
              <Field label={_t("Occasione")} value={v.purpose} />
              <Field label={_t("Beneficiario")} value={`${v.beneficiary_name} ${v.beneficiary_surname || ""}`.trim()} />
              <Field label={_t("Email beneficiario")} value={v.beneficiary_email_masked} />
              <Field label={_t("Emittente")} value={v.bank_name} />
              <Field label={_t("Firma digitale")} value={v.signature_status?.toUpperCase() || "—"} />
            </div>

            <div className="pt-6 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{_t("Impronta hash del PDF")}</div>
              <div className="font-mono-launch text-xs text-white/60 break-all">{v.pdf_sha256 || "—"}</div>
            </div>

            <div className="mt-6 p-4 rounded-lg bg-white/5 border border-white/10 text-xs text-white/60 flex items-start gap-3">
              <ShieldCheck className="w-4 h-4 text-[#FF5722] mt-0.5 shrink-0" />
              <div>{_t("Il possesso del PDF")}{" "}<strong>{_t("non consente")}</strong>{" "}{_t("il riscatto. Solo il beneficiario autenticato può riscuotere il valore. Per segnalazioni di frode, contatta il supporto.")}</div>
            </div>
          </div>
        )}

        {/* Upload PDF verification */}
        <div className="tc-card p-6 mt-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3"><Upload className="w-3.5 h-3.5" />{" "}{_t("Verifica un PDF caricando il file")}</div>
          <label htmlFor="pdf-upload" data-testid="pdf-upload-label" className="cursor-pointer block border border-dashed border-white/15 hover:border-[#FF5722] rounded-xl p-6 text-center transition-colors">
            <input id="pdf-upload" type="file" accept="application/pdf" onChange={uploadPdf} className="hidden" />
            <div className="text-sm text-white mb-1">{_t("Carica il PDF del voucher")}</div>
            <div className="text-xs text-white/50">{_t("Compareremo l'hash con quello registrato")}</div>
          </label>
          {uploadResult && !uploadResult.loading && (
            <div className={`mt-4 p-4 rounded-xl text-sm ${uploadResult.match ? "bg-green-500/10 border border-green-500/30 text-green-200" : "bg-red-500/10 border border-red-500/30 text-red-200"}`}>
              {uploadResult.match ? <CheckCircle2 className="w-4 h-4 inline mr-2" /> : <XCircle className="w-4 h-4 inline mr-2" />}
              {uploadResult.match ? _t("Il documento corrisponde al voucher registrato.") : (uploadResult.reason || _t("Il documento non corrisponde a nessun voucher registrato."))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">{label}</div>
      <div className={`${highlight ? "font-display text-2xl text-[#FF5722]" : "text-white"}`}>{value}</div>
    </div>
  );
}
